/**
 * Page ExerciseDetail — vue lecture seule d'un procédé.
 * Route : /dashboard/seances/insightlab/:id
 */
import { useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, Pencil, Copy, Trash2, MoreVertical,
  AlertCircle, Loader2,
} from 'lucide-react'
import DashboardLayout from '../components/DashboardLayout'
import PitchEditor from '../components/PitchEditor'
import { useAuth } from '../context/AuthContext'
import {
  useProcede, useDuplicateProcede, useDeleteProcede,
} from '../hooks/useProcedes'
import { T } from '../theme'

const PROCEDE_LABELS = {
  echauffement: 'Échauffement',
  jeu: 'Jeu',
  situation: 'Situation',
  exercice: 'Exercice',
  match: 'Match',
}

export default function ExerciseDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: exo, loading, error, refetch } = useProcede(id)
  const { mutate: doDuplicate, loading: dupLoading } = useDuplicateProcede()
  const { mutate: doDelete, loading: delLoading } = useDeleteProcede()
  const [menuOpen, setMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  if (loading) return <DashboardLayout><Center><Loader2 size={28} className="spin" color={T.gold} /></Center></DashboardLayout>
  if (error || !exo) return (
    <DashboardLayout>
      <Center>
        <AlertCircle size={32} color={T.red} />
        <p style={{ fontFamily: T.mono, fontSize: 12, color: T.ink, margin: '12px 0' }}>
          {error || 'Exercice introuvable.'}
        </p>
        <button
          onClick={() => refetch()}
          style={btnGhostStyle}
        >Réessayer</button>
      </Center>
    </DashboardLayout>
  )

  const isOwner = exo.user_id === user?.id
  const isSystem = exo.is_template && exo.user_id === null
  const canEdit = isOwner && !isSystem
  const canDelete = canEdit

  const handleDuplicate = async () => {
    try {
      const copy = await doDuplicate(exo.id)
      navigate(`/dashboard/seances/insightlab/${copy.id}/edit`)
    } catch {
      alert('Échec de la duplication.')
    }
  }
  const handleDelete = async () => {
    if (!confirm(`Supprimer définitivement "${exo.name}" ?`)) return
    try {
      await doDelete(exo.id)
      navigate('/dashboard/seances?tab=insightlab')
    } catch (e) {
      alert(e?.message || 'Échec de la suppression.')
    }
  }

  const procedeLabel = PROCEDE_LABELS[exo.procede] || exo.procede
  const hasObjectives = exo.objectives && (
    (exo.objectives.offensive?.length || 0) + (exo.objectives.defensive?.length || 0) > 0
  )

  // Métadonnées compactes
  const meta = []
  if (exo.default_duration_minutes) meta.push(`${exo.default_duration_minutes} min`)
  if (exo.default_effectif) meta.push(exo.default_effectif)
  if (exo.default_surface) meta.push(exo.default_surface)
  if (exo.charge_params?.intensite) meta.push(`Charge ${exo.charge_params.intensite}`)
  const metaStr = meta.join(' · ')

  return (
    <DashboardLayout>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`}</style>
      <div style={{ padding: '20px 24px 60px', maxWidth: 1280, margin: '0 auto' }}>
        {/* Top bar */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 16, gap: 8, flexWrap: 'wrap',
        }}>
          <Link
            to="/dashboard/seances?tab=insightlab"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              color: T.ink, textDecoration: 'none',
              fontFamily: T.mono, fontSize: 11, letterSpacing: '.12em',
              textTransform: 'uppercase', fontWeight: 700,
            }}
          >
            <ArrowLeft size={13} /> Séances
          </Link>

          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {canEdit && (
              <Link
                to={`/dashboard/seances/insightlab/${exo.id}/edit`}
                style={{
                  background: T.gold, color: T.dark, border: `2px solid ${T.dark}`,
                  boxShadow: `4px 4px 0 ${T.dark}`,
                  padding: '8px 14px', textDecoration: 'none',
                  fontFamily: T.mono, fontSize: 10, fontWeight: 700,
                  letterSpacing: '.14em', textTransform: 'uppercase',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                <Pencil size={12} /> Modifier
              </Link>
            )}
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setMenuOpen(v => !v)}
                style={btnGhostStyle}
                aria-label="Actions"
              >
                <MoreVertical size={14} />
              </button>
              {menuOpen && (
                <DropMenu
                  items={[
                    { icon: <Copy size={12} />, label: 'Dupliquer', onClick: handleDuplicate, disabled: dupLoading },
                    ...(canDelete ? [
                      { divider: true },
                      { icon: <Trash2 size={12} />, label: 'Supprimer', danger: true, onClick: handleDelete, disabled: delLoading },
                    ] : []),
                  ]}
                  onClose={() => setMenuOpen(false)}
                />
              )}
            </div>
          </div>
        </div>

        {/* Header titre */}
        <div style={{ marginBottom: 24 }}>
          <div style={{
            fontFamily: T.mono, fontSize: 10, letterSpacing: '.18em',
            textTransform: 'uppercase', color: T.gold, fontWeight: 700, marginBottom: 6,
          }}>
            {procedeLabel}{exo.category_tactique ? ` · ${exo.category_tactique}` : ''}
            {isSystem && <span style={{ marginLeft: 12, color: T.muted2 }}>· Template Insightball</span>}
          </div>
          <h1 style={{
            fontFamily: T.display, fontSize: isMobile ? 24 : 38, color: T.ink,
            textTransform: 'uppercase', letterSpacing: '.01em',
            lineHeight: 1, margin: 0,
          }}>
            {exo.name}
          </h1>
          {metaStr && (
            <div style={{
              fontFamily: T.mono, fontSize: 12, color: T.muted2,
              letterSpacing: '.04em', marginTop: 8,
            }}>
              {metaStr}
            </div>
          )}
        </div>

        {/* Layout 2 cols */}
        <div style={{
          display: 'grid', gap: 20,
          gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 2fr) minmax(0, 1fr)',
        }}>
          {/* Colonne gauche : terrain */}
          <div style={{
            background: T.surface, border: `2px solid ${T.dark}`,
            boxShadow: `4px 4px 0 ${T.dark}`, padding: 16,
          }}>
            <PitchEditor
              initialSchema={exo.schema}
              initialOrientation={exo.pitch_orientation || 'landscape'}
              readOnly
              showToolbar={false}
            />
          </div>

          {/* Colonne droite : objectifs + organisation */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {hasObjectives && (
              <Block title="Objectifs">
                {exo.objectives.offensive?.length > 0 && (
                  <SubBlock label="Offensifs" items={exo.objectives.offensive} />
                )}
                {exo.objectives.defensive?.length > 0 && (
                  <SubBlock label="Défensifs" items={exo.objectives.defensive} />
                )}
              </Block>
            )}
            {exo.organization && (
              <Block title="Organisation">
                <p style={parStyle}>{exo.organization}</p>
              </Block>
            )}
            {exo.description && (
              <Block title="Description">
                <p style={parStyle}>{exo.description}</p>
              </Block>
            )}
          </div>
        </div>

        {/* Sections pleines largeur */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 20 }}>
          {exo.deroulement && (
            <Block title="Déroulement">
              <p style={{ ...parStyle, whiteSpace: 'pre-wrap' }}>{exo.deroulement}</p>
            </Block>
          )}
          {exo.consignes && (
            <Block title="Consignes">
              <p style={{ ...parStyle, whiteSpace: 'pre-wrap' }}>{exo.consignes}</p>
            </Block>
          )}
          {exo.variantes?.length > 0 && (
            <Block title="Variantes">
              <ul style={listStyle}>
                {exo.variantes.map((v, i) => <li key={i}>{v}</li>)}
              </ul>
            </Block>
          )}
          {exo.charge_params && Object.keys(exo.charge_params).length > 0 && (
            <Block title="Charge">
              <ChargeView c={exo.charge_params} />
            </Block>
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}


// ─────────────────────────────────────────────────────────────────────
// Sous-composants
// ─────────────────────────────────────────────────────────────────────

const btnGhostStyle = {
  background: T.surface, color: T.ink, border: `2px solid ${T.dark}`,
  padding: '8px 12px', cursor: 'pointer',
  fontFamily: T.mono, fontSize: 11, fontWeight: 700,
  letterSpacing: '.12em', textTransform: 'uppercase',
  display: 'inline-flex', alignItems: 'center', gap: 6,
}

const parStyle = {
  fontFamily: T.mono, fontSize: 12, color: T.ink2,
  lineHeight: 1.6, margin: 0,
}

const listStyle = {
  fontFamily: T.mono, fontSize: 12, color: T.ink2,
  lineHeight: 1.7, margin: 0, paddingLeft: 20,
}

function Center({ children }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: 60, textAlign: 'center',
    }}>{children}</div>
  )
}

function Block({ title, children }) {
  return (
    <div style={{
      background: T.surface, border: `2px solid ${T.dark}`,
      boxShadow: `4px 4px 0 ${T.dark}`, padding: '16px 18px',
    }}>
      <h2 style={{
        fontFamily: T.display, fontSize: 16, color: T.ink,
        textTransform: 'uppercase', letterSpacing: '.04em',
        margin: '0 0 12px', paddingBottom: 6, borderBottom: `2px solid ${T.dark}`,
      }}>
        {title}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {children}
      </div>
    </div>
  )
}

function SubBlock({ label, items }) {
  return (
    <div>
      <div style={{
        fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em',
        textTransform: 'uppercase', color: T.gold, marginBottom: 4, fontWeight: 700,
      }}>
        {label}
      </div>
      <ul style={listStyle}>
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  )
}

function ChargeView({ c }) {
  const Row = ({ k, v }) => v ? (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 8,
      padding: '4px 0', borderBottom: `1px solid ${T.rule}`,
    }}>
      <span style={{
        fontFamily: T.mono, fontSize: 10, color: T.muted2,
        letterSpacing: '.08em', textTransform: 'uppercase',
      }}>{k}</span>
      <span style={{ fontFamily: T.mono, fontSize: 11, color: T.ink, fontWeight: 600 }}>
        {v}
      </span>
    </div>
  ) : null
  return (
    <div>
      <Row k="Intensité"           v={c.intensite} />
      <Row k="Temps de travail"    v={c.temps_travail} />
      <Row k="Récupération"        v={c.temps_recup} />
      <Row k="Répétitions"         v={c.repetitions} />
      <Row k="Mode récup."         v={c.mode_recup} />
    </div>
  )
}

function DropMenu({ items, onClose }) {
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 30 }} />
      <div style={{
        position: 'absolute', right: 0, top: '110%', zIndex: 31,
        background: T.surface, border: `2px solid ${T.dark}`,
        boxShadow: `4px 4px 0 ${T.dark}`, minWidth: 180,
      }}>
        {items.map((it, i) => it.divider ? (
          <div key={i} style={{ height: 1, background: T.rule }} />
        ) : (
          <button
            key={i}
            type="button"
            disabled={it.disabled}
            onClick={() => { it.onClick(); onClose() }}
            style={{
              width: '100%', padding: '8px 12px', textAlign: 'left',
              background: 'transparent', border: 'none',
              cursor: it.disabled ? 'not-allowed' : 'pointer',
              opacity: it.disabled ? 0.5 : 1,
              display: 'flex', alignItems: 'center', gap: 8,
              fontFamily: T.mono, fontSize: 11,
              color: it.danger ? T.red : T.ink,
            }}
          >
            {it.icon} {it.label}
          </button>
        ))}
      </div>
    </>
  )
}
